/**
 * Beat 13, *It Fails* — everything that moves in the dawn yard, as pure
 * fixed-step simulation (`docs/beat-13-plan.md`). No meshes, no audio, no
 * Rapier: the room module draws what this decides, and the sim lane replays
 * the whole beat without a browser.
 *
 * **Nothing here reads the player except the surge's starting point.** Every
 * mover is staged on the stage clock the story hands it (`enter`), so a
 * player holding every key produces the same stage ticks, the same jam, the
 * same bell and the same exit as one who presses nothing (plan gate 4). The
 * surge starts from wherever she stands because the plan's surge is *her*
 * being carried, and a carry that began by teleporting her to a mark would be
 * the one cut in the beat.
 *
 * **No mover is a watcher.** Kestner and the dogs have no cones and there is
 * no `Watcher` type in this file's imports; S-11 is structural (D-201), and
 * `beat-13-fails.spec.ts` checks the room hands the host no stealth at all.
 */

import type { CarriedPose, SpawnPoint } from '@/physics/character';
import type { CompanionSnapshot } from '@/systems/companion';
import {
  dawnHerdLanePointAt, TIMBER_PATCHED_BAY, VEHICLE_GATE, VEHICLE_GATE_OPEN_ANGLE, vehicleGateLeafTip, YARD,
} from '@/world/loadingYard';
import { HOLLY_HOLD_STATION } from '@/story/act3/dawnHolly';

export type ItFailsStage = 'the-jam' | 'the-whistle' | 'the-bell' | 'the-surge' | 'outside';

/**
 * Where Holly's exit crosses the yard: what she faces at the open, so a
 * player who never touches the stick has the bell stage in front of them.
 * The first frame faced the race gate and Holly's whole run left the lens
 * past its left edge.
 */
const LOOKING_AT = { x: 5.5, z: 7.0 } as const;

/**
 * Where she stands when the beat opens: near the gateway (the handover), 2 m
 * off the herd's string and 3.8 m into the yard. Closer, the camera's arm
 * met the gateway and the east fence behind her and folded to 0.6 m, looking
 * straight down at her back for the whole bell stage.
 */
const SPAWN_AT = { x: VEHICLE_GATE.centreX - 0.75, z: YARD.maxZ - 3.8 } as const;
export const IT_FAILS_SPAWN: SpawnPoint = {
  x: SPAWN_AT.x,
  y: 0.25,
  z: SPAWN_AT.z,
  // Looking back into the yard, across the herd to Holly holding the race:
  // the three things the handover contract puts on screen (beat 12's close).
  facing: Math.atan2(LOOKING_AT.x - SPAWN_AT.x, LOOKING_AT.z - SPAWN_AT.z),
};

// ── The gate ──────────────────────────────────────────────────────────────
/**
 * Where the left leaf stops: ~40° from shut, half across the gateway (the plan:
 * *"it jams half-shut, the wrong way, on the herd's own press"*).
 */
export const GATE_JAM_ANGLE = 0.7;
/** The swing starts after the camera has found him, and takes this long. */
export const GATE_SWING_FROM = 2.2;
export const GATE_SWING_SECONDS = 2.4;

// ── Kestner ───────────────────────────────────────────────────────────────
/**
 * By the west gatepost at the open leaf's free end, where a man who knew
 * which gate she would use would wait. He walks the leaf's tip across and
 * stays there: from the jam on he IS the gateway (the plan's first line).
 * On the leaf's north-west side, off the herd's lane: the first frame put
 * him 0.4 m from it, standing inside a pig. From here he is 1.0–1.4 m clear
 * of the string for the whole swing.
 */
function kestnerAtAngle(angle: number): { x: number; z: number } {
  const tip = vehicleGateLeafTip('left', angle);
  return { x: tip.x - 0.55, z: tip.z + 0.35 };
}
export const KESTNER_START = kestnerAtAngle(VEHICLE_GATE_OPEN_ANGLE.left);
export const KESTNER_JAMMED = kestnerAtAngle(GATE_JAM_ANGLE);

// ── Holly ─────────────────────────────────────────────────────────────────
/**
 * Her trot away from Rosalie, toward the dogs and past them, and out behind
 * the truck. Waypoints with arrival times in seconds from `the-bell`: a
 * trot, ~1.4 m/s, with a beat of standing at the start where she lets the
 * leaf go. The last point is behind the trailer's west end, out of every
 * frame the yard has, and she is not drawn again after the stage.
 */
export const HOLLY_EXIT: readonly { readonly x: number; readonly z: number; readonly t: number }[] = [
  { x: HOLLY_HOLD_STATION.x, z: HOLLY_HOLD_STATION.z, t: 0 },
  { x: HOLLY_HOLD_STATION.x, z: HOLLY_HOLD_STATION.z, t: 0.9 },
  { x: 4.6, z: 9.4, t: 4.2 },
  { x: 0.6, z: 9.8, t: 7.1 },
  { x: -4.2, z: 6.2, t: 11.4 },
];
/** Where the last two bells are rung from, in `outside`: beyond her last waypoint, further off. */
export const OFFSTAGE_BELL_AT = { x: -8.5, y: 0.55, z: 3.5 } as const;
/** `outside`'s two rings, seconds into the stage, and then it stops (the plan). */
export const OFFSTAGE_BELLS = [1.4, 3.3] as const;

// ── The dogs ──────────────────────────────────────────────────────────────
/** Round the truck from the north fence (the plan), the far side from her. */
export const DOG_STARTS = [{ x: -3.6, z: 13.1 }, { x: -4.6, z: 12.3 }] as const;
/**
 * Where the whistle sends them: the milling herd's west flank. Holly's hold
 * station is 1.7 m from the first, so when she lets the leaf go she trots
 * toward the dogs and past them (the plan), and they turn after her.
 */
export const DOG_FLANKS = [{ x: 7.8, z: 6.8 }, { x: 8.4, z: 4.8 }] as const;
/** A working dog's run. Faster than Holly's trot, so the bell stage is a chase that she leads. */
export const DOG_SPEED = 3.4;
/** How long after the first bell each dog breaks off the herd for her: the sound reaching them. */
export const DOG_TURN_LAG = [1.6, 2.2] as const;

// ── The herd ──────────────────────────────────────────────────────────────
/** Visible members. The rest of beat 12's forty are behind the race, in the shed. */
export const IT_FAILS_HERD_COUNT = 14;
/** The head's lane distance at the open: pressing into the gateway, still inside z = YARD.maxZ. */
export const HERD_HEAD_AT_OPEN = 9.4;
const HERD_SPACING = 1.5;
/**
 * Where the turned herd mills (stage 2–3): back against the race they came
 * out of. Not the yard's middle: the first frames put the knot on the line
 * from her to Holly, and the bell stage was pigs' backs with a sheep's bell
 * somewhere behind them. `MILL_SPREAD` keeps its edge 1.5 m off that line.
 */
export const HERD_MILL_CENTRE = { x: 10.2, z: 6.0 } as const;
const MILL_SPREAD = 0.55;
/**
 * Stage 4's pack: rows along the north fence either side of the patched bay.
 * The front row's centre is `PACK_FRONT_Z` so a 1.15 m animal's snout reaches
 * the boards and no member is ever north of the fence (plan gate 6).
 */
export const PACK_FRONT_Z = YARD.maxZ - 0.62;
/** How much further the member at the gap is pressed in: its centre stays 0.2 m inside the fence. */
const PACK_GAP_PUSH = 0.42;

// ── Her ───────────────────────────────────────────────────────────────────
/**
 * The carried route, as fixed marks the surge bends through. `bay` is the
 * gap under the patched boards (`TIMBER_PATCHED_BAY`, beat 7's exit) and
 * `outside` is where the hold finds her, a body length beyond it.
 */
export const SURGE_BAY_APPROACH = { x: TIMBER_PATCHED_BAY.x + 1.3, z: YARD.maxZ - 0.75 } as const;
export const SURGE_THROUGH = { x: TIMBER_PATCHED_BAY.x, z: YARD.maxZ + 0.55 } as const;
/**
 * Feet on the ground: `y` is where her feet go, and the carry resets her
 * there every tick, so the spawn's usual 0.25 m drop would hold her floating
 * for the whole shot (the first surge frame did).
 */
export const IT_FAILS_OUTSIDE: SpawnPoint = { x: TIMBER_PATCHED_BAY.x - 0.25, y: 0, z: YARD.maxZ + 1.45, facing: Math.PI };
/** Arrival times inside the 6 s surge. Pressed along, then through in a rush. */
const SURGE_MARKS = { approach: 3.6, bay: 4.4, through: 5.2, outside: 5.9 } as const;

export interface HerdPose { readonly x: number; readonly z: number; readonly heading: number; readonly moving: boolean }
export interface MoverPose { readonly x: number; readonly z: number; readonly yaw: number; readonly moving: boolean }

export interface ItFailsSnapshot {
  readonly stage: ItFailsStage;
  readonly stageTime: number;
  /** The left vehicle-gate leaf's angle from shut. The pivot and the collider both read this. */
  readonly gateAngle: number;
  readonly jammed: boolean;
  readonly kestner: MoverPose;
  readonly dogs: readonly MoverPose[];
  readonly dogsVisible: boolean;
  readonly holly: CompanionSnapshot;
  readonly hollyVisible: boolean;
  readonly herd: readonly HerdPose[];
  /** Her body, while the beat has it (the surge and the hold); null while she is the player's. */
  readonly carry: CarriedPose | null;
}

const smooth = (t: number): number => {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
};
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

function along(points: readonly { readonly x: number; readonly z: number; readonly t: number }[], time: number): { x: number; z: number; yaw: number; moving: boolean } {
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from === undefined || to === undefined) break;
    if (time <= to.t) {
      const span = to.t - from.t;
      const k = span <= 0 ? 1 : (time - from.t) / span;
      const moving = Math.hypot(to.x - from.x, to.z - from.z) > 0.01;
      return { x: lerp(from.x, to.x, k), z: lerp(from.z, to.z, k), yaw: moving ? Math.atan2(to.x - from.x, to.z - from.z) : HOLLY_HOLD_STATION.yaw, moving };
    }
  }
  const last = points[points.length - 1];
  const before = points[points.length - 2];
  if (last === undefined || before === undefined) throw new Error('it fails: a route needs two points');
  return { x: last.x, z: last.z, yaw: Math.atan2(last.x - before.x, last.z - before.z), moving: false };
}

/** Member `i`'s place in the string at the open: beat 12's lane, head in the gateway. */
function flowPose(index: number, press: number): HerdPose {
  const at = dawnHerdLanePointAt(HERD_HEAD_AT_OPEN + press - index * HERD_SPACING);
  return { x: at.x, z: at.z, heading: at.heading, moving: false };
}

/** The milling knot, on a golden-angle spiral: deterministic, no member on another. */
function millPose(index: number, time: number): HerdPose {
  const radius = MILL_SPREAD * Math.sqrt(index + 0.5);
  const angle = index * 2.399963;
  // A slow sway, so a held herd is not a still life. Its phase is the member's
  // own, and its amplitude is centimetres: they are packed, not grazing.
  const sway = Math.sin(time * 1.3 + index * 1.7) * 0.12;
  return {
    x: HERD_MILL_CENTRE.x + Math.cos(angle) * radius + sway,
    z: HERD_MILL_CENTRE.z + Math.sin(angle) * radius,
    // Turned back from the dogs' side, into the yard: heads south and east.
    heading: Math.PI * 0.78 + Math.sin(index * 3.1) * 0.5,
    moving: false,
  };
}

/** Stage 4–5's pack along the north fence, either side of the bay, snouts at the boards. */
function packPose(index: number, time: number): HerdPose {
  const row = index % 3;
  const column = Math.floor(index / 3);
  // Columns alternate either side of the bay so the gap itself holds the
  // front-row centre member: that is the one whose snout is at the gap.
  const side = column % 2 === 0 ? 1 : -1;
  const offset = column === 0 ? 0 : side * (0.62 + Math.floor((column + 1) / 2) * 0.66);
  const nudge = Math.sin(time * 2.3 + index * 2.1) * 0.05;
  // The one at the gap pushes its head into it: its snout clears the fence
  // line and its centre does not (gate 6 counts centres).
  const atGap = index === 0 ? PACK_GAP_PUSH : 0;
  return {
    x: TIMBER_PATCHED_BAY.x + offset + (row - 1) * 0.28,
    z: PACK_FRONT_Z - row * 0.95 + (row === 0 ? nudge : 0) + atGap,
    heading: row === 0 ? 0 : Math.sin(index * 1.9) * 0.4,
    moving: false,
  };
}

function blendHerd(from: HerdPose, to: HerdPose, t: number): HerdPose {
  const k = smooth(t);
  const moving = t > 0 && t < 1;
  // Face where they are going while they go; settle into the target's heading on arrival.
  const travel = Math.atan2(to.x - from.x, to.z - from.z);
  const settle = smooth((t - 0.75) / 0.25);
  const heading = moving ? lerpAngle(travel, to.heading, settle) : k >= 1 ? to.heading : from.heading;
  return { x: lerp(from.x, to.x, k), z: lerp(from.z, to.z, k), heading, moving };
}

function lerpAngle(a: number, b: number, t: number): number {
  const d = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + d * t;
}

/** Stage lengths, in seconds. Owned by the story; passed in so there is one copy. */
export interface ItFailsClocks { readonly surge: number }

export class ItFailsStaging {
  #stage: ItFailsStage = 'the-jam';
  #stageTime = 0;
  /**
   * Nothing moves until the story's first cue. The room ticks from boot, but
   * the sequencer is held while a chapter card is up (`main.ts`), and a gate
   * that swung behind the card would be a jam nobody saw.
   */
  #started = false;
  #hollyTravelled = 0;
  #holly: CompanionSnapshot = { x: HOLLY_HOLD_STATION.x, z: HOLLY_HOLD_STATION.z, yaw: HOLLY_HOLD_STATION.yaw, moving: false, phase: 'at-high-latch', travelledDistance: 0 };
  readonly #dogs: { x: number; z: number; yaw: number; moving: boolean }[] = DOG_STARTS.map((at) => ({ x: at.x, z: at.z, yaw: Math.PI / 2, moving: false }));
  /** Each member's pose on the step its current transition began. */
  #herdFrom: HerdPose[] = Array.from({ length: IT_FAILS_HERD_COUNT }, (_, index) => flowPose(index, 0));
  #herd: HerdPose[] = this.#herdFrom.slice();
  #surgeFrom: { x: number; z: number } | null = null;
  #carry: CarriedPose | null = null;
  readonly #clocks: ItFailsClocks;

  constructor(clocks: ItFailsClocks) {
    this.#clocks = clocks;
  }

  /**
   * The story entered `stage`. `her` is read ONCE, here, and only for the
   * surge: it is where the carry starts. Every other stage ignores it.
   */
  enter(stage: ItFailsStage, her: { readonly x: number; readonly z: number }): void {
    this.#started = true;
    this.#stage = stage;
    this.#stageTime = 0;
    this.#herdFrom = this.#herd.slice();
    if (stage === 'the-surge') this.#surgeFrom = { x: her.x, z: her.z };
  }

  step(dt: number): void {
    if (!this.#started) return;
    this.#stageTime += dt;
    const t = this.#stageTime;
    this.#stepHerd(t);
    this.#stepHolly(t);
    this.#stepDogs(dt, t);
    const was = this.#carry;
    const at = this.#carryAt(t);
    // Pressed along the fence she WALKS: `character.reset` zeroes her speed, so
    // without the carry's own pace she slid the whole shot in an idle
    // (`CarriedPose.speed`). Measured from the last tick, not the route's
    // segment, so the first surge tick (nothing to measure from) and the
    // `outside` hold (not moving) both stand.
    const speed = at !== null && was !== null && dt > 0 ? Math.hypot(at.x - was.x, at.z - was.z) / dt : 0;
    this.#carry = at === null || speed <= 0 ? at : { ...at, speed };
  }

  #stepHerd(t: number): void {
    const count = IT_FAILS_HERD_COUNT;
    for (let index = 0; index < count; index += 1) {
      const from = this.#herdFrom[index] ?? flowPose(index, 0);
      let pose: HerdPose;
      switch (this.#stage) {
        case 'the-jam': {
          // The press: the head shoves on for the swing, then the string
          // concertinas to a stop against the leaf. Nothing more comes through.
          const press = 0.35 * smooth((t - GATE_SWING_FROM) / GATE_SWING_SECONDS) + Math.sin(t * 3.1 + index) * 0.03;
          pose = { ...flowPose(index, press * Math.max(0, 1 - index * 0.08)), moving: t < GATE_SWING_FROM + GATE_SWING_SECONDS };
          break;
        }
        case 'the-whistle':
          // The dogs arrive at ~3 s; the string breaks from its tail first,
          // which is the end nearest them.
          pose = blendHerd(from, millPose(index, t), (t - 2.2 - (count - index) * 0.12) / 3.6);
          break;
        case 'the-bell':
          pose = millPose(index, t + 8);
          break;
        case 'the-surge':
          // Turned by the dogs' first run, they pack toward the north fence and
          // she is in it: the front of the pack arrives with her.
          pose = blendHerd(from, packPose(index, t), (t - index * 0.07) / 3.9);
          break;
        case 'outside':
          pose = packPose(index, t + 6);
          break;
      }
      this.#herd[index] = pose;
    }
  }

  #stepHolly(t: number): void {
    if (this.#stage !== 'the-bell') return;
    const at = along(HOLLY_EXIT, t);
    this.#hollyTravelled += Math.hypot(at.x - this.#holly.x, at.z - this.#holly.z);
    // `following` is the trot's phase for the presenter; the hold is over the
    // tick she steps off the leaf.
    this.#holly = { x: at.x, z: at.z, yaw: at.yaw, moving: at.moving, phase: at.moving ? 'following' : 'at-high-latch', travelledDistance: this.#hollyTravelled };
  }

  #stepDogs(dt: number, t: number): void {
    if (this.#stage !== 'the-whistle' && this.#stage !== 'the-bell') {
      for (const dog of this.#dogs) dog.moving = false;
      return;
    }
    this.#dogs.forEach((dog, index) => {
      let target: { x: number; z: number } | undefined;
      if (this.#stage === 'the-whistle') target = DOG_FLANKS[index];
      else {
        // The bell reaches them, and they go to the sound: her trail, a
        // little behind her. Beat 10 taught the player this; here Holly
        // runs it herself (the plan).
        const lag = DOG_TURN_LAG[index] ?? 2;
        target = t < lag ? DOG_FLANKS[index] : along(HOLLY_EXIT, t - lag * 0.6);
      }
      if (target === undefined) return;
      const dx = target.x - dog.x;
      const dz = target.z - dog.z;
      const distance = Math.hypot(dx, dz);
      const reach = DOG_SPEED * dt;
      dog.moving = distance > 0.05;
      if (distance > 0.001) dog.yaw = Math.atan2(dx, dz);
      const k = distance <= reach ? 1 : reach / distance;
      dog.x += dx * k;
      dog.z += dz * k;
    });
  }

  #carryAt(t: number): SpawnPoint | null {
    if (this.#stage === 'outside') return IT_FAILS_OUTSIDE;
    if (this.#stage !== 'the-surge' || this.#surgeFrom === null) return null;
    const from = this.#surgeFrom;
    const marks = [
      { x: from.x, z: from.z, t: 0 },
      { x: SURGE_BAY_APPROACH.x, z: SURGE_BAY_APPROACH.z, t: SURGE_MARKS.approach },
      { x: TIMBER_PATCHED_BAY.x, z: YARD.maxZ - 0.25, t: SURGE_MARKS.bay },
      { x: SURGE_THROUGH.x, z: SURGE_THROUGH.z, t: SURGE_MARKS.through },
      { x: IT_FAILS_OUTSIDE.x, z: IT_FAILS_OUTSIDE.z, t: Math.min(SURGE_MARKS.outside, this.#clocks.surge) },
    ];
    const at = along(marks, t);
    return { x: at.x, y: IT_FAILS_OUTSIDE.y, z: at.z, facing: at.yaw };
  }

  snapshot(): ItFailsSnapshot {
    const gateAngle = this.#gateAngle();
    const inYard = this.#stage === 'the-jam' || this.#stage === 'the-whistle' || this.#stage === 'the-bell';
    const kestnerK = smooth((this.#stage === 'the-jam' ? this.#stageTime - GATE_SWING_FROM : GATE_SWING_SECONDS) / GATE_SWING_SECONDS);
    return {
      stage: this.#stage,
      stageTime: this.#stageTime,
      gateAngle,
      jammed: this.#stage !== 'the-jam' || this.#stageTime >= GATE_SWING_FROM + GATE_SWING_SECONDS,
      kestner: {
        x: lerp(KESTNER_START.x, KESTNER_JAMMED.x, kestnerK),
        z: lerp(KESTNER_START.z, KESTNER_JAMMED.z, kestnerK),
        // Facing the gateway he is closing, then the yard he has closed.
        yaw: kestnerK < 1 ? Math.PI * 0.5 : Math.PI * 0.85,
        moving: kestnerK > 0 && kestnerK < 1,
      },
      dogs: this.#dogs.map((dog) => ({ ...dog })),
      // From the whistle, not the jam: they are its answer. Parked at their
      // starts through the jam, two dogs stood waiting by the north fence for a
      // player who turned round (Codex review of beat 13, session 69).
      dogsVisible: this.#stage === 'the-whistle' || this.#stage === 'the-bell',
      holly: this.#holly,
      hollyVisible: inYard,
      herd: this.#herd,
      carry: this.#carry,
    };
  }

  #gateAngle(): number {
    if (this.#stage !== 'the-jam') return GATE_JAM_ANGLE;
    const k = smooth((this.#stageTime - GATE_SWING_FROM) / GATE_SWING_SECONDS);
    return lerp(VEHICLE_GATE_OPEN_ANGLE.left, GATE_JAM_ANGLE, k);
  }
}
